// Phase 4.1 (hh-13c) — JitOnboardingScreen.
//
// Ported from designproject/files/screens-onboard.jsx (`JitOnboardingScreen`,
// `MiniTreeRow`). case-40: первый запуск — одна вещь, два конца сценария за
// минуту (положил голосом → нашёл). Дизайн был полностью скриптовым; здесь шаг
// «сказать голосом» открывает реальное голосовое модальное окно, а сама сессия
// онбординга персистится через POST /api/v1/onboarding (mode=jit), чтобы
// прохождение фиксировалось на бэке.

import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { T, Icon, Btn, Avatar, type IconName } from '../../tokens';
import type { RouterApi } from '../../router';
import { onboarding } from '../../api';

export interface JitOnboardingScreenProps {
  readonly router: RouterApi;
  /** Opens the voice-dock and starts recording (the JIT «say it» step). */
  readonly onOpenVoice: () => void;
}

interface Step {
  readonly title: string;
  readonly body: string;
  readonly cta: string;
  readonly icon: IconName;
  readonly action?: 'voice';
}

const STEPS: readonly Step[] = [
  {
    title: 'Привет 👋',
    body: 'Я помогу не забывать, где что лежит и что когда обслуживать.',
    cta: 'Поехали',
    icon: 'spark',
  },
  {
    title: 'Скажи одну вещь, которую боишься забыть',
    body: 'Например: «положил паспорт в верхний ящик стола в спальне». Я запомню — и ты сможешь спросить.',
    cta: 'Сказать голосом',
    icon: 'mic',
    action: 'voice',
  },
  {
    title: 'Запомнил 👍',
    body: 'Сказанное превратилось в запись: вещь, место и сам объект создались автоматически — загляни в дерево имущества.',
    cta: 'Спросить «где это?»',
    icon: 'check',
  },
  {
    title: 'Так это работает 🔎',
    body: 'Положил голосом → спросил → нашлось. Дальше — добавляй остальное так же, в своём темпе.',
    cta: 'Открыть приложение',
    icon: 'search',
  },
];

const TREE_ROWS: readonly { label: string; icon: IconName; depth: number; highlight?: boolean }[] = [
  { label: 'Квартира', icon: 'home', depth: 0 },
  { label: 'Спальня', icon: 'folder', depth: 1 },
  { label: 'Верхний ящик стола', icon: 'box', depth: 2 },
  { label: 'Паспорт', icon: 'item', depth: 3, highlight: true },
];

export function JitOnboardingScreen({
  router,
  onOpenVoice,
}: JitOnboardingScreenProps): JSX.Element {
  const [step, setStep] = useState(0);
  const sessionId = useRef<string | null>(null);

  // Сессия онбординга — фиксируем старт и завершение на бэке. Сбой сети не
  // должен ломать сам экран, поэтому ошибки проглатываются.
  useEffect(() => {
    onboarding
      .startOnboarding('jit')
      .then((s) => {
        sessionId.current = s.id;
      })
      .catch(() => undefined);
  }, []);

  const finish = (): void => {
    if (sessionId.current !== null) {
      void onboarding.completeOnboarding(sessionId.current).catch(() => undefined);
    }
    router.resetTo('feed');
  };

  const s = STEPS[step];
  if (s === undefined) return <div />;
  const isLast = step === STEPS.length - 1;

  const advance = (): void => {
    if (isLast) {
      finish();
      return;
    }
    if (s.action === 'voice') {
      // Открываем voice-dock — пользователь делает первую запись.
      onOpenVoice();
    }
    setStep(step + 1);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <div style={{ padding: '14px 20px 0', display: 'flex', gap: 4 }}>
        {STEPS.map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background: i <= step ? T.brand : T.borderStrong,
            }}
          />
        ))}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '32px 24px', overflow: 'auto' }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: 24,
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft2}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
            }}
          >
            <Icon name={s.icon} size={36} color={T.brandDeep} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 750, letterSpacing: -0.6, color: T.ink, lineHeight: 1.15 }}>
            {s.title}
          </div>
          <div style={{ fontSize: 15, color: T.ink2, marginTop: 12, lineHeight: 1.5 }}>{s.body}</div>

          {step === 2 && (
            <div
              style={{
                marginTop: 20,
                padding: 14,
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 14,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: T.ink3,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                что появилось
              </div>
              <div style={{ marginTop: 8 }}>
                {TREE_ROWS.map((r) => (
                  <MiniTreeRow key={r.label} {...r} />
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div
              style={{
                marginTop: 20,
                padding: 16,
                background: T.brandSoft,
                border: `1px solid ${T.brandSoft2}`,
                borderRadius: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Avatar kind="ai" icon="pin" size={36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Паспорт</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                  Верхний ящик · Спальня · Квартира
                </div>
              </div>
            </div>
          )}
        </div>

        <Btn
          kind="primary"
          size="lg"
          {...(isLast ? {} : { iconAfter: 'chevR' as const })}
          onClick={advance}
          style={{ width: '100%' }}
        >
          {s.cta}
        </Btn>
        {step > 0 && !isLast && (
          <Btn kind="ghost" size="md" onClick={finish} style={{ marginTop: 4, width: '100%' }}>
            Пропустить
          </Btn>
        )}
      </div>
    </div>
  );
}

interface MiniTreeRowProps {
  readonly label: string;
  readonly icon: IconName;
  readonly depth: number;
  readonly highlight?: boolean;
}

function MiniTreeRow({ label, icon, depth, highlight = false }: MiniTreeRowProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 0',
        marginLeft: depth * 16,
      }}
    >
      <Icon name={icon} size={14} color={highlight ? T.brand : T.ink3} />
      <div
        style={{
          fontSize: 13,
          fontWeight: highlight ? 700 : 500,
          color: highlight ? T.brand : T.ink,
        }}
      >
        {label}
      </div>
    </div>
  );
}
